import { deleteOrganizationUsersApi } from "@/api/services/admin/organization";
import { updateUserApi, useForceChangePassword, useResendUserInvitation, useUserDetail } from "@/api/services/admin/user";
import { TUpdateUserReqDto } from "@/api/services/admin/user/dtos/update-user.dto";
import { useEnum } from "@/api/services/global/enum";
import { IconOngoingMail, IconSave, IconTrash } from "@/assets/icons";
import { getPICGuardOffenders, TCannotDeactivateUser, UserCannotDeactivateModal } from "@/components/commons/UserCannotDeactivateModal";
import { Button } from "@/components/elements/Button";
import { DetailItem } from "@/components/elements/DetailItem";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { Modal } from "@/components/elements/Modal";
import { Select } from "@/components/elements/Select";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { EUserAccountStatus, EAdminUserStatus, EUserProductContractStatus } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { UserProductSelect } from "@/features/organization/components/UserProductSelect";
import { updateOrganizationUserSchema } from "@/features/organization/schema/updateOrganizationUserSchema";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { EContractStatus } from "@/types/enum";
import { isApiError, setErrorForm, showError } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CardContent, CardHeader, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Resolver, useController, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TFormValues = {
  fullName: string;
  newEmail: string;
  role: string;
  status: string;
  productContractIds: number[];
};

const OrganizationUserEditPage = () => {
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteDialog = useDialog();
  const passwordResetDialog = useDialog();
  const { visible: CannotDeactivateOpen, open: openCannotDeactivate, close: closeCannotDeactivate } = useVisible(false);
  const [usersCannotDeactivate, setUsersCannotDeactivate] = useState<TCannotDeactivateUser[]>([]);
  const [errorMessage, setErrorMessage] = useState("");

  const { data, isLoading } = useUserDetail();
  const { data: accountStatuses } = useEnum("EUserAccountStatus");

  const { mutateAsync: forceChangePassword, isPending: isForcingToChangePass } = useForceChangePassword();
  const { mutateAsync: resendUserInvitation, isPending: isResendingInvitation } = useResendUserInvitation();

  const user = data?.data;
  const userProductContracts = user?.productContracts ?? [];
  const isPasswordResetDisabled =
    !user?.isPasswordRegistered ||
    user?.accountStatus === EUserAccountStatus.INVITATION_SENT ||
    user?.accountStatus === EUserAccountStatus.PASSWORD_RESET;

  const methods = useForm<TFormValues>({
    defaultValues: { fullName: "", newEmail: "", role: "", status: "", productContractIds: [] },
    resolver: yupResolver(updateOrganizationUserSchema) as unknown as Resolver<TFormValues>,
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, reset } = methods;

  const {
    field: { value: productContractIds, onChange: onProductContractIdsChange },
  } = useController({ control, name: "productContractIds" });

  useEffect(() => {
    if (!user) return;
    reset({
      fullName: user.fullName ?? "",
      newEmail: user.newEmail ?? "",
      role: user.role ?? "",
      status: user.status ?? "",
      productContractIds: userProductContracts.flatMap((contract) => (contract.status === EUserProductContractStatus.ACTIVE ? [contract.id] : [])),
    });
  }, [user, reset]);

  const handleBack = () => {
    navigate(`${getDynamicRoute(AppRoutes.organizationDetail, { id })}?tab=users`);
  };

  const onSubmit = async (formData: TFormValues) => {
    if (!userId) return;
    setErrorMessage("");
    try {
      const { newEmail, ...rest } = formData;
      const payload: TUpdateUserReqDto = {
        ...rest,
        newEmail: newEmail?.trim() ? newEmail.trim() : null,
      };
      await updateUserApi(Number(userId), payload);
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_DETAIL, userId] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_LIST] });
      handleBack();
    } catch (error) {
      if (isApiError(error)) {
        setErrorMessage(error.message);
      }
      // Blocked because the user is an org's 担当者 — show the offender in a modal.
      const offenders = getPICGuardOffenders(error);
      if (offenders) {
        setUsersCannotDeactivate(offenders);
        openCannotDeactivate();
        return;
      }
      setErrorForm(methods, error);
      showError(error);
    }
  };

  const handleForceChangePassword = async () => {
    if (!userId || isForcingToChangePass) return;

    const confirmed = await passwordResetDialog.show();
    if (!confirmed) return;

    try {
      await forceChangePassword({ userId: Number(userId) });
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_DETAIL, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_ADMIN_LIST, userId] });
    } catch (error) {
      showError(error);
    }
  };

  const handleResendInvitation = async () => {
    if (!userId || isResendingInvitation) return;
    try {
      await resendUserInvitation(Number(userId));
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_DETAIL, userId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_ADMIN_LIST, userId] });
    } catch (error) {
      showError(error);
    }
  };

  const handleDelete = async () => {
    setErrorMessage("");
    if (!userId) return;
    const confirmed = await deleteDialog.show();
    if (!confirmed) return;
    try {
      await deleteOrganizationUsersApi([Number(userId)]);
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_LIST] });
      handleBack();
    } catch (error) {
      if (isApiError(error)) {
        setErrorMessage(error.message);
      }
      // Blocked because the user is an org's 担当者 — show the offender in a modal.
      const offenders = getPICGuardOffenders(error);
      if (offenders) {
        setUsersCannotDeactivate(offenders);
        openCannotDeactivate();
        return;
      }
      showError(error);
    }
  };

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "ユーザーの状態",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  ユーザーの状態
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={4.8}>
                  <FormField control={control} name="status">
                    <Select enumName="EAdminUserStatus" />
                  </FormField>
                </Grid>
                <Grid item xs={4.8}>
                  <DetailItem label="認証ステータス" value={findLabelByValue(accountStatuses, user?.accountStatus)} />
                </Grid>
                <Grid
                  item
                  xs={2.4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={isPasswordResetDisabled || isForcingToChangePass}
                    isLoading={isForcingToChangePass}
                    onClick={handleForceChangePassword}
                  >
                    パスワードリセット
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
      {
        title: "基本情報",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  基本情報
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormField control={control} name="fullName">
                    <Input />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="role">
                    <Select enumName="EUserRole" />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField label="現在のメールアドレス">
                    <Input readonly value={user?.email} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="newEmail">
                    <Input />
                  </FormField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
      {
        title: "プロダクトアクセス権限",
        content: (
          <UserProductSelect
            userProductContracts={userProductContracts}
            selectedContractIds={productContractIds ?? []}
            onSelectedContractIdsChange={onProductContractIdsChange}
            includeContractStatuses={[EContractStatus.DURING_CONTRACT, EContractStatus.TRIAL]}
          />
        ),
      },
    ],
    [control, accountStatuses, user, isForcingToChangePass, productContractIds, onProductContractIdsChange],
  );

  if (isLoading) return <LoadingPage />;

  return (
    <>
      <Form methods={methods} onSubmit={onSubmit} schema={updateOrganizationUserSchema}>
        <Stack gap={3}>
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
            <Stack gap={1}>
              <Typography variant="head20Bold" color={text.primary}>
                顧客ユーザーの編集
              </Typography>
              <Typography variant="body14" color={text.secondary}>
                顧客側画面にログインできるユーザーアカウントに関する情報を編集できます。
              </Typography>
            </Stack>
          </Stack>

          <MenuNavigate
            contents={contents}
            contentAffix={
              <StickyFooter justifyContent="space-between">
                <Button size="sm" variant="ghost" onClick={handleBack}>
                  戻る
                </Button>

                <Stack flexDirection="row" gap={2}>
                  <Button size="sm" variant="danger" symbol={<IconTrash fontSize="14px" />} onClick={handleDelete}>
                    削除
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={
                      isResendingInvitation ||
                      !(user?.accountStatus === EUserAccountStatus.INVITATION_SENT && user?.status !== EAdminUserStatus.DISABLED)
                    }
                    isLoading={isResendingInvitation}
                    onClick={handleResendInvitation}
                    symbol={<IconOngoingMail color="currentColor" width={20} height={20} />}
                  >
                    再招待
                  </Button>
                  <Button size="sm" type="submit" symbol={<IconSave color="currentColor" />}>
                    保存
                  </Button>
                </Stack>
              </StickyFooter>
            }
          />
        </Stack>
      </Form>
      <Modal
        isOpen={deleteDialog.open}
        title="顧客ユーザーの削除"
        labelOk="削除する"
        labelClose="キャンセル"
        onOk={deleteDialog.confirm}
        onClose={deleteDialog.cancel}
      >
        <Typography variant="cap12" color={text.secondary} py={2} component="div">
          この顧客ユーザーを削除しますか？この操作は取り消せません。
        </Typography>
      </Modal>
      <Modal
        isOpen={passwordResetDialog.open}
        title="パスワードリセット"
        labelOk="リセットする"
        labelClose="キャンセル"
        onOk={passwordResetDialog.confirm}
        onClose={passwordResetDialog.cancel}
      >
        <Typography variant="cap12" color={text.secondary} py={2} component="div">
          登録済みパスワードがリセットされます。再登録のためのURLが本人にメールで通知されます。
          <br />
          続行してよろしいですか。
        </Typography>
      </Modal>

      <UserCannotDeactivateModal
        isOpen={CannotDeactivateOpen}
        onClose={closeCannotDeactivate}
        users={usersCannotDeactivate}
        errorMessage={errorMessage}
      />
    </>
  );
};

export default OrganizationUserEditPage;
